import { createApi } from '@reduxjs/toolkit/query/react';

import { ApiResponse } from '../../model';
import { API_CONSTANTS } from '../../utils/constants';

import type {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LinkVerificationResponse,
  LoginRequest,
  LoginResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
  ValidateUserNameRequest,
  ValidateUserNameResponse,
  OnboardingValidationResponse,
  CompleteOnboardingPayload,
  VerifyOtpRequest,
} from './model';
import { baseQueryWithReauth } from '../../apis/baseQuery';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    validateUserName: builder.mutation<
      ApiResponse<ValidateUserNameResponse>,
      ValidateUserNameRequest
    >({
      query: ({ email }) => ({
        url: API_CONSTANTS.VALIDATE_EMAIL_ENDPOINT,
        method: 'POST',
        body: { email },
        showSuccessToast: false,
      }),
    }),

    loginWithPassword: builder.mutation<
      ApiResponse<LoginResponse>,
      LoginRequest
    >({
      query: ({ email, password }) => ({
        url: API_CONSTANTS.LOGIN_ENDPOINT,
        method: 'POST',
        body: { email, password },
      }),
    }),

    forgetPassword: builder.mutation<
      ApiResponse<ForgotPasswordResponse>,
      ForgotPasswordRequest
    >({
      query: ({ email }) => ({
        url: API_CONSTANTS.FORGOT_PASSWORD_ENDPOINT,
        method: 'POST',
        body: { email },
      }),
    }),
    verifyLink: builder.mutation<ApiResponse<LinkVerificationResponse>, string>(
      {
        query: (token) => ({
          url: API_CONSTANTS.LINK_VERIFICATION_ENDPOINT,
          method: 'POST',
          body: { token },
        }),
      }
    ),
    verifyOnboardingCode: builder.query<
      ApiResponse<OnboardingValidationResponse>,
      string
    >({
      query: (code) => ({
        url: `${API_CONSTANTS.ONBOARDING_VERIFY_ENDPOINT}/${code}`,
        method: 'GET',
      }),
    }),
    resetPassword: builder.mutation<
      ApiResponse<ResetPasswordResponse>,
      ResetPasswordPayload
    >({
      query: (body) => ({
        url: API_CONSTANTS.RESET_PASSWORD_ENDPOINT,
        method: 'POST',
        body,
      }),
    }),
    verifyOtp: builder.mutation<
      ApiResponse<Record<string, unknown>>,
      VerifyOtpRequest
    >({
      query: ({ email, otp }) => ({
        url: API_CONSTANTS.VERIFY_OTP_ENDPOINT,
        method: 'POST',
        body: { email, otp },
      }),
    }),
    completeOnboarding: builder.mutation<
      ApiResponse<Record<string, unknown>>,
      CompleteOnboardingPayload
    >({
      query: (body) => ({
        url: API_CONSTANTS.ONBOARDING_COMPLETE_ENDPOINT,
        method: 'POST',
        body,
      }),
    }),
    logout: builder.mutation<ApiResponse<any>, void>({
      query: () => {
        const token = localStorage.getItem('access_token');
        return {
          url: API_CONSTANTS.LOGOUT_ENDPOINT,
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
          showSuccessToast: false,
        };
      },
    }),
  }),
});

export const {
  useValidateUserNameMutation,
  useLoginWithPasswordMutation,
  useForgetPasswordMutation,
  useVerifyLinkMutation,
  useVerifyOnboardingCodeQuery,
  useResetPasswordMutation,
  useVerifyOtpMutation,
  useCompleteOnboardingMutation,
  useLogoutMutation,
} = authApi;
